import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@platform/design-system';
import { Mail, Pause, Play, UserMinus } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSheet } from '../FormSheet.tsx';
import type { Subsidiary } from '../settings/subsidiaries.ts';
import { usePageContext } from '../usePageContext.ts';
import { AccessFields } from './AccessFields.tsx';
import {
  accessBodyOf,
  accessFormOf,
  accessFormSchema,
  displayNameOf,
  type AccessBody,
  type AccessFormValues,
  type Member,
} from './team.ts';
import styles from './Team.module.css';

export type MemberCommand = 'pause' | 'resume' | 'resend' | 'remove';

export type MemberSheetProps = {
  member: Member;
  subsidiaries: readonly Subsidiary[];
  isAdmin: boolean;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (body: AccessBody) => void;
  onCommand: (command: MemberCommand) => void;
};

export const MemberSheet = (props: MemberSheetProps) => {
  const { member, subsidiaries, isAdmin, submitting, errorMessage, onClose, onSubmit, onCommand } = props;
  const { t } = usePageContext();
  const form = useForm<AccessFormValues>({
    resolver: zodResolver(accessFormSchema),
    defaultValues: accessFormOf(member),
  });

  return (
    <FormSheet
      open
      formId="member-form"
      title={displayNameOf(member)}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(accessBodyOf(values));
        })(event);
      }}
    >
      <FormProvider {...form}>
        <p className={styles['note']} dir="ltr">
          {member.email}
        </p>
        <AccessFields subsidiaries={subsidiaries} isAdmin={isAdmin} />
      </FormProvider>
      <div className={styles['actions']}>
        {member.invitationPending ? (
          <Button
            variant="outline"
            icon={<Mail size={20} aria-hidden="true" />}
            disabled={submitting}
            onClick={() => onCommand('resend')}
          >
            {t('team.resend')}
          </Button>
        ) : null}
        {member.active ? (
          <Button
            variant="outline"
            icon={<Pause size={20} aria-hidden="true" />}
            disabled={submitting}
            onClick={() => onCommand('pause')}
          >
            {t('team.pause')}
          </Button>
        ) : (
          <Button
            variant="outline"
            icon={<Play size={20} aria-hidden="true" />}
            disabled={submitting}
            onClick={() => onCommand('resume')}
          >
            {t('team.resume')}
          </Button>
        )}
        <Button
          variant="dangerGhost"
          icon={<UserMinus size={20} aria-hidden="true" />}
          disabled={submitting}
          onClick={() => onCommand('remove')}
        >
          {t('team.remove')}
        </Button>
      </div>
    </FormSheet>
  );
};
